import {notSet, toArray} from "@/utils/ValueUtils";
import {ValidationError} from "./ValidationError";
import {Expressions} from "./Expressions";
import {Classes, Types} from "./Constants";
import {Validator} from "./Validator";

export interface ValidationResult {
    readonly isValid: boolean;
    readonly isWarning: boolean;
    readonly message: string | null | undefined;
}

type ValidationFailureFunc = (res: ValidationResult) => void;

export class Validators {
    static readonly expressions = Expressions; // Prefer using direct imports
    static readonly types = Object.freeze(Types); // Prefer using direct imports
    static readonly classes = Object.freeze(Classes); // Prefer using direct imports

    private static retrieveValidators(element: HTMLElement) {
        if (!element) {
            return [];
        }
        // FUTURE: Is this still necessary?
        // Hybrid implementation to allow JSDOM to work in unit tests
        const s = element.dataset === undefined
            ? element.getAttribute("data-validators") // NOSONAR
            : element.dataset.validators;
        if (!s) {
            return [];
        }
        return s.split(",")
            .map((s) => s.trim())
            .filter((s) => !(!s));
    }

    private static setValidators(element?: HTMLElement | null, typesArray?: string[] | null) {
        if (!element) {
            return;
        }

        const stringValue = Array.isArray(typesArray) ? typesArray.join(",") : null;
        // FUTURE: Is this still necessary?
        // Hybrid implementation to allow JSDOM to work in unit tests
        if (element.dataset !== undefined) {
            if (stringValue) {
                element.dataset.validators = stringValue;
            } else {
                delete element.dataset.validators;
            }
        } else if (stringValue) {
            element.setAttribute("data-validators", stringValue); // NOSONAR
        } else {
            element.removeAttribute("data-validators"); // NOSONAR
        }
    }

    static hasValidator(
        element: HTMLElement | null | undefined,
        validatorType: string | null | undefined,
    ): boolean {
        if (!element || !validatorType) {
            return false;
        }
        return Validators.retrieveValidators(element).includes(validatorType);
    }

    static addValidatorAttribute(
        element: HTMLElement | null | undefined,
        validatorType: string | null | undefined,
    ): void {
        if (!element || !validatorType) {
            return;
        }

        const toAdd = String(validatorType).trim();
        const validators = Validators.retrieveValidators(element);
        if (!validators.includes(toAdd)) {
            validators.push(toAdd);
        }
        Validators.setValidators(element, validators);
    }

    static removeValidatorAttribute(
        element: HTMLElement | null | undefined,
        validatorType: string | null | undefined,
    ): void {
        if (!element || !validatorType) {
            return;
        }

        const toRemove = String(validatorType).trim();
        const validators = Validators.retrieveValidators(element);
        Validators.setValidators(element, validators.filter((s) => s && s !== toRemove));
    }

    static addValidators(objs: unknown) {
        const elements = toArray(objs) as HTMLElement[];
        const validators: Validator[] = [];
        for (const el of elements) {
            const types = Validators.retrieveValidators(el);
            if (!types || types.length === 0) {
                continue;
            }
            validators.push(new Validator(el, types));
        }
        return validators;
    }

    static removeValidators(objs: unknown) {
        const elements = toArray(objs) as HTMLElement[];
        for (const el of elements) {
            this.setValidators(el, null);
        }
    }

    static addEventListeners(validators: Validator[]) {
        for (const validator of validators) {
            validator.init();
        }
    }

    static clearErrors(parentEl: HTMLElement): void {
        if (!parentEl) {
            return;
        }
        for (const el of parentEl.querySelectorAll("." + Classes.ERROR)) {
            Validators.clearError(el);
        }
    }

    static clearError(node: Element): void {
        node?.classList.remove(Classes.ERROR);
    }

    // FUTURE: Remove support for JQuery
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static isJQueryObject(context: any): boolean {
        // Original logic: return context instanceof jQuery

        // Avoid using the "real" jQuery object type and instanceof as we
        // do not want to import jQuery here if we can avoid it
        return !!context
            && typeof context === "object"
            && typeof context.on === "function"
            && typeof context.jquery === "string";
    }

    // FUTURE: Remove support for JQuery as a parameter type (a number of callers need updating)
    static load(context: HTMLElement | JQuery | null | undefined): Validator[] {
        const validators: Validator[] = [];
        if (!context) {
            console.error('Missing context parameter for locating elements for validation. No validators loaded.');
            return validators;
        }

        const contextEl = Validators.isJQueryObject(context)
            ? (context as JQuery)[0]
            : context as HTMLElement;
        for (const el of contextEl.querySelectorAll<HTMLElement>("[data-validators]")) {
            validators.push(new Validator(el, this.retrieveValidators(el)));
        }
        return validators;
    }

    // Note that this method does not completely support the "warning" validation type
    static validateNoAlert(validators: Validator[] | null | undefined, e?: Event): boolean {
        return callValidate(validators ?? [], e);
    }

    // NOTE: If you are looking for the validate() function that displays a dialogue on error,
    // see ValidatorsWithDialogs.

    /**
     * Performs validation of the provided validators, returning a promise
     * that resolves when complete.
     *
     * @param validators The validators returned from load()
     * @param fromParentEl The parent of the elements in the validators
     * (only elements under this parent will be validated).
     * @returns Resolves with a boolean indicating if the result was to
     * display a warning or not. If the promise rejects, then validation failed.
     */
    static async validateAsync(validators: Validator[], fromParentEl?: HTMLElement | null): Promise<boolean> {
        const res = performValidation(validators, fromParentEl);
        if (!res.isValid) {
            throw new ValidationError(res.message);
        }
        return res.isWarning;
    }

    // Note that this method does support the "warning" validation type
    static validateOne(element: HTMLElement | null | undefined): boolean {
        if (notSet(element)) {
            // No element, so always passes validation
            return true;
        }
        const validators = Validators.retrieveValidators(element);
        if (!validators || validators.length === 0) {
            // No validation definition, so always "passes"
            return true;
        }
        return new Validator(element, validators).validate().isValid;
    }
}

function performValidation(validators: Validator[], fromParentEl?: HTMLElement | null): ValidationResult {
    let result = true;
    let showWarning = false;
    let message: string | null = null;

    for (const validator of validators) {
        if (fromParentEl && !fromParentEl.contains(validator.element)) {
            continue;
        }
        const isValidationDisabled = validator.element.getAttribute("do-validation") === "false";
        if (!isValidationDisabled) {
            const r = validator.validate();
            if (r.message && !message) {
                message = r.message;
            }
            if (r.isValid && validator.inError()) {
                // marked as "in error", but must be a warning as isValid is true (validation succeeded)
                showWarning = true;
            }
            result = r.isValid && result;
        }
    }
    return {
        isValid: result,
        isWarning: showWarning,
        message: message
    };
}

// Note that this method does not completely support the "warning" validation type
export function callValidate(
    validators: Validator[],
    e?: Event | null,
    fromParentEl?: HTMLElement | null,
    onFailureFunc?: ValidationFailureFunc | null,
): boolean {
    const res = performValidation(validators, fromParentEl);

    if (!res.isValid) {
        if (typeof onFailureFunc === "function") {
            onFailureFunc(res);
        }
        if (e) {
            e.preventDefault?.();
            e.stopImmediatePropagation?.();
        }
    }
    return res.isValid;
}
